import * as React from 'react';
import { TextInput, type TextInputProps } from '@ministry/ui/internal';
import Eye from 'lucide-react/dist/esm/icons/eye.js';
import EyeOff from 'lucide-react/dist/esm/icons/eye-off.js';

import { useT } from '../i18n/strings.js';

/**
 * SCREEN_SPECS.md I1: "password input (with show/hide toggle)". `@ministry/ui`'s
 * 32-component inventory has no dedicated password field — this composes the existing
 * `TextInput` primitive rather than adding a 33rd design-system component for one
 * screen.
 *
 * ux-review defect (chrome pass): the toggle used to be a right-aligned underlined
 * text link BELOW the field ("पासवर्ड देखाउनुहोस्"), which read as a footnote rather
 * than a control belonging to the field. It now uses `TextInput`'s `endSlot` (added in
 * this pass, see that component's own comment) to place an eye icon INSIDE the field —
 * the established pattern — without any fixed pixel offset that could drift when
 * `errorText`/`helperText` change the field's height. `aria-label` states the ACTION
 * ("Show password"/"Hide password"), and `aria-pressed` reflects whether the value is
 * currently visible, so a screen reader user hears both what the control does next and
 * what state it is in now.
 *
 * `autoComplete` defaults to `"current-password"` (the login-screen case this component
 * was built for) but a caller MAY override it — the account-settings password-change form
 * needs `"new-password"` on its two new-password fields, and WCAG 1.3.5 (Identify Input
 * Purpose) is the wrong reason to get that wrong: a browser/password-manager that thinks a
 * NEW password field is the CURRENT one will offer to autofill the old password into it.
 */
export type PasswordFieldProps = Omit<TextInputProps, 'type' | 'endSlot'>;

export const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(function PasswordField(
  { autoComplete = 'current-password', ...props },
  ref,
) {
  const t = useT();
  const [visible, setVisible] = React.useState(false);

  return (
    <TextInput
      {...props}
      ref={ref}
      type={visible ? 'text' : 'password'}
      autoComplete={autoComplete}
      endSlot={
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? t('login.hidePassword') : t('login.showPassword')}
          aria-pressed={visible}
          className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-md text-text-muted hover:bg-surface-hover hover:text-text-primary focus-visible:outline-none focus-visible:shadow-focus"
        >
          {visible ? <EyeOff aria-hidden="true" width={18} height={18} /> : <Eye aria-hidden="true" width={18} height={18} />}
        </button>
      }
    />
  );
});
